<template>
  <div class="dashboard-page" :class="{ 'client-shell': isClientRole }">
    <!-- Hub client patrimoine : progression, KPI, conseiller -->
    <template v-if="isClientRole">
      <header v-if="!clientError" class="dashboard-page__client-header">
        <h1 class="client-greeting">{{ clientGreeting }}</h1>
        <p class="client-greeting__sub">{{ clientGreetingSub }}</p>
      </header>

      <router-link
        v-if="!clientLoading && !clientError && financialDocumentsComplete === false && clientDossierState !== DOSSIER_STATE.QUESTIONNAIRE"
        :to="{ name: 'Mon analyse patrimoniale', query: { goto: 'documents' } }"
        class="client-docs-reminder"
      >
        <DocumentTextIcon class="client-docs-reminder__icon" aria-hidden="true" />
        <span>
          Il manque des justificatifs à votre dossier (résidence, revenu, relevé bancaire).
          <strong>Les ajouter</strong>
        </span>
      </router-link>

      <section class="dashboard-page__stats dashboard-page__stats--client">
        <template v-if="clientLoading">
          <div class="client-hub">
            <div class="client-hub__main">
              <SkeletonLoader variant="card" />
              <div class="dashboard-page__grid dashboard-page__grid--client">
                <SkeletonLoader v-for="i in clientStatCards.length" :key="'client-stat-' + i" variant="card" />
              </div>
            </div>
            <SkeletonLoader class="client-hub__rail" variant="card" />
          </div>
        </template>
        <template v-else-if="clientError">
          <div class="dashboard-page__error">
            <p class="text-red-600 font-medium">{{ clientError }}</p>
          </div>
        </template>
        <template v-else>
          <div class="client-hub">
            <div class="client-hub__main">
              <div class="client-card client-status">
                <div
                  class="client-gauge"
                  :style="{ '--client-gauge-value': dossierProgressPercent }"
                  role="img"
                  :aria-label="`Avancement du dossier : ${dossierProgressPercent} %`"
                >
                  <span class="client-gauge__value">{{ dossierProgressPercent }}%</span>
                </div>
                <div class="client-status__body">
                  <h2 class="client-status__title">{{ dossierStatusCard.title }}</h2>
                  <p class="client-status__text">{{ dossierStatusCard.text }}</p>
                  <p v-if="dossierStatusCard.note" class="client-status__note">
                    <LockClosedIcon class="client-status__note-icon" aria-hidden="true" />
                    {{ dossierStatusCard.note }}
                  </p>
                </div>
                <router-link
                  :to="dossierStatusCard.to"
                  class="client-btn"
                  :class="dossierStatusCard.ghost ? 'client-btn--ghost' : 'client-btn--primary'"
                >
                  {{ dossierStatusCard.cta }}
                </router-link>
              </div>

              <div v-if="clientBilanLocked" class="client-card client-empty">
                <span class="client-empty__icon" aria-hidden="true">
                  <LockClosedIcon />
                </span>
                <h2 class="client-empty__title">Vos bilans sont protégés par un code</h2>
                <p class="client-empty__text">
                  Saisissez le code de validation à 6 chiffres reçu par e-mail pour afficher vos
                  indicateurs et consulter vos bilans. Il reste valable 24 h.
                </p>
                <router-link :to="resultatsRoute" class="client-btn client-btn--primary">
                  Saisir mon code
                </router-link>
              </div>

              <div v-else-if="duoCoupleProgress" class="client-card client-empty">
                <span class="client-empty__icon" aria-hidden="true">
                  <UserGroupIcon />
                </span>
                <h2 class="client-empty__title">Bilan couple : {{ duoCoupleProgress.percent }}% complété</h2>
                <p class="client-empty__text">{{ duoCoupleProgressMessage }}</p>
              </div>

              <template v-else>
                <section class="client-section">
                  <div class="dashboard-page__section-head">
                    <h2 class="client-section__label">Vos indicateurs</h2>
                    <button
                      type="button"
                      class="dashboard-page__refresh-btn"
                      :disabled="clientRefreshing"
                      :aria-busy="clientRefreshing"
                      @click="refreshClientKpis"
                    >
                      <span v-if="clientRefreshing" class="dashboard-page__refresh-spinner" aria-hidden="true" />
                      {{ clientRefreshing ? 'Actualisation…' : 'Actualiser' }}
                    </button>
                  </div>
                  <div class="dashboard-page__grid dashboard-page__grid--client">
                    <StatCard
                      v-for="(card, idx) in clientStatCards"
                      :key="`${card.key}-${duoDashboardView}-${resolvedClientOffer}`"
                      class="dashboard-page__stat-card-entrance"
                      :style="{ animationDelay: `${idx * 0.08 }s` }"
                      soft
                      :label="card.label"
                      :value="card.value"
                      :icon="card.icon"
                      :accent="card.accent"
                      :suffix="card.suffix"
                      :status="card.status"
                      :status-badge="card.statusBadge"
                      :tooltip="card.tooltip"
                    />
                  </div>
                  <p class="dashboard-page__stats-note">
                    {{ hasClientIndicators
                      ? 'Calculé à partir de votre dernière analyse.'
                      : 'Ces indicateurs s’afficheront dès la publication de votre analyse.' }}
                  </p>
                </section>

                <section class="client-section">
                  <h2 class="client-section__label">Revenus vs dépenses</h2>
                  <div class="card-revenus-depenses dashboard-page__revenus-depenses">
                    <div class="card-revenus-depenses__content">
                      <div v-if="revenusDepensesChartReady" class="card-revenus-depenses__chart-wrap">
                        <div v-show="hasRevenusOrDepenses" ref="revenusDepensesChartRef" class="ct-chart card-revenus-depenses__chart"></div>
                        <div v-if="!hasRevenusOrDepenses" class="card-revenus-depenses__empty">
                          <span class="card-revenus-depenses__empty-icon" aria-hidden="true">
                            <ChartBarIcon />
                          </span>
                          <p class="card-revenus-depenses__empty-text">
                            {{ hasClientIndicators
                              ? 'Aucun revenu ni dépense déclaré dans votre dernière analyse.'
                              : 'Ce graphique s’affichera dès la publication de votre première analyse.' }}
                          </p>
                          <router-link :to="chartEmptyCta.to" class="client-btn client-btn--ghost">
                            {{ chartEmptyCta.label }}
                          </router-link>
                        </div>
                        <div v-if="hasRevenusOrDepenses" class="card-revenus-depenses__legend-inline">
                          <span class="card-revenus-depenses__legend-item card-revenus-depenses__legend--revenus">
                            <span class="card-revenus-depenses__dot"></span> Revenus : {{ formatMontant(clientStats.revenusAgreges) }} F CFA
                          </span>
                          <span class="card-revenus-depenses__legend-item card-revenus-depenses__legend--depenses">
                            <span class="card-revenus-depenses__dot"></span> Dépenses : {{ formatMontant(clientStats.depensesAgregees) }} F CFA
                          </span>
                        </div>
                      </div>
                      <div v-else class="card-revenus-depenses__empty">
                        <p class="text-slate-500 text-sm">Chargement des données…</p>
                      </div>
                      <p v-if="revenusDepensesContext.message" class="card-revenus-depenses__message" :class="revenusDepensesContext.critical ? 'card-revenus-depenses__message--critical' : ''">
                        {{ revenusDepensesContext.message }}
                      </p>
                      <p v-if="revenusDepensesContext.recommendation" class="card-revenus-depenses__recommendation">
                        {{ revenusDepensesContext.recommendation }}
                      </p>
                      <p class="card-revenus-depenses__period">{{ revenusDepensesPeriodLabel }}</p>
                    </div>
                  </div>
                </section>
              </template>
            </div>

            <aside class="client-hub__rail">
              <div class="client-card client-advisor client-advisor--navy">
                <span class="client-advisor__avatar" aria-hidden="true">
                  <ChatBubbleLeftRightIcon />
                </span>
                <div class="client-advisor__body">
                  <p class="client-advisor__name">Votre conseiller</p>
                  <p class="client-advisor__meta">
                    Un conseiller Patrimonea suit votre dossier. Posez vos questions : la réponse vous parvient par message.
                  </p>
                </div>
                <router-link :to="contactConseillerRoute" class="client-btn client-btn--advisor">
                  Contacter mon conseiller
                </router-link>
              </div>
              <!-- <div
                v-for="teaser in premiumTeasers"
                :key="teaser.key"
                class="client-card client-advisor client-teaser--navy"
              >
                <span class="client-advisor__avatar" aria-hidden="true">
                  <DocumentDownloadIcon />
                </span>
                <div class="client-advisor__body">
                  <p class="client-advisor__name">{{ teaser.title }}</p>
                  <p class="client-advisor__meta">{{ teaser.text }}</p>
                </div>
                <router-link :to="profilRisqueRoute" class="client-btn client-btn--advisor">
                  {{ teaser.cta }}
                </router-link>
              </div> -->
            </aside>
          </div>
        </template>
      </section>
    </template>

    <!-- Unified dashboard (admin / employe): stats + master-detail -->
    <template v-if="showStaffDashboard">
      <!-- Stats: Total Clients, Pending Signatures, Incomplete Files, Active Offers -->
      <section class="dashboard-page__stats">
        <template v-if="loading">
          <div class="dashboard-page__grid dashboard-page__grid--stats">
            <SkeletonLoader v-for="i in 9" :key="'stat-' + i" variant="card" />
          </div>
        </template>
        <template v-else-if="error">
          <div class="dashboard-page__error">
            <p class="text-red-600 font-medium">{{ error }}</p>
          </div>
        </template>
        <template v-else>
          <div class="dashboard-page__grid dashboard-page__grid--stats">
            <StatCard
              v-for="stat in unifiedStatCards"
              :key="stat.key"
              :label="stat.label"
              :value="stat.value"
              :icon="stat.icon"
              :accent="stat.accent"
              :to="stat.to"
            />
          </div>
        </template>
      </section>

      <!-- Master-detail: client list (left) + detail panel (right) -->
      <section class="dashboard-page__master-detail">
        <div class="dashboard-page__master-wrap">
          <ClientMasterList
            :selected-id="selectedClient?.login || selectedClient?.username || null"
            :anonymize="!isAdminRole"
            @select="onSelectClient"
          />
        </div>
        <div class="dashboard-page__detail-wrap">
          <ClientDetailPanel
            :client="selectedClient"
            :doc-status="selectedClientDocStatus"
            :recent-logs="selectedClientLogs"
            :signatures="selectedClientSignatures"
            :hide-client-name="!isAdminRole"
            @view-all-logs="openAllLogs"
            @view-all-signatures="openAllSignatures"
          />
        </div>
      </section>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onActivated, onBeforeUnmount, watch, nextTick, inject } from 'vue';
import { useRoute } from 'vue-router';
import {
  UserGroupIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  ChartBarIcon,
  ChartPieIcon,
  EnvelopeIcon,
  UsersIcon,
  Cog6ToothIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  DocumentDownloadIcon,
  LockClosedIcon,
} from '@heroicons/vue/24/outline';
import axios from 'axios';
import StatCard from './StatCard.vue';
import SkeletonLoader from './SkeletonLoader.vue';
import ClientMasterList from './ClientMasterList.vue';
import ClientDetailPanel from './ClientDetailPanel.vue';
import Chartist from 'chartist';
import 'chartist/dist/chartist.min.css';

import { getUserRole } from '@/utils/auth';
import { isAuthenticatedSession } from '@/utils/sessionAuth';
import { syncEmployeProfileFromApi } from '@/utils/permissions';
import { getErrorMessage, safeErrorMessage } from '@/utils/errorMessages';
import { parseDuoDashboardView } from '@/utils/duoDashboardView';
import { DOSSIER_STATE, deriveDossierState, normalizeLatestAnalyse } from '@/utils/dossierState';
import { WIZARD_STEP_TOTAL, wizardStepNumber, wizardStepLabel } from '@/utils/wizardSteps';
import { MODULE1_CLIENT_OFFER_KEY, MODULE1_CLIENT_FORMULE_KEY } from '@/features/patrimoine/injectionKeys';
import { catalogOfferId, formuleFromApi, normalizePayableOffer, offerLabel } from '@/utils/paymentPricing';

const route = useRoute();
/** Réf. fournie par DashboardLayout : type_offre client (disponible dès que le layout a répondu). */
const injectedClientOffer = inject(MODULE1_CLIENT_OFFER_KEY, null);
const injectedClientFormule = inject(MODULE1_CLIENT_FORMULE_KEY, null);
const loading = ref(true);
const error = ref(null);

const clientLoading = ref(true);
const clientRefreshing = ref(false);
const clientError = ref(null);
/**
 * Le backend refuse les bilans tant que le code de validation n'a pas été saisi
 * (403 `bilan_code_required`). Ce n'est pas une panne : on propose la saisie du
 * code au lieu d'afficher une erreur rouge.
 */
const clientBilanLocked = ref(false);
/**
 * Onglet Couple (Duo) tant que les 2 conjoints n'ont pas soumis leur bilan :
 * { percent: 0|50, ordre1Done: bool, ordre2Done: bool }. Statut de complétion
 * seul, sans chiffre financier — accessible sans code de validation (backend).
 * `null` = non applicable (autre onglet, ou bilan couple complet à 100%).
 */
const duoCoupleProgress = ref(null);
const duoCoupleProgressMessage = computed(() => {
  const p = duoCoupleProgress.value;
  if (!p) return '';
  if (p.percent === 0) return 'Aucun des deux conjoints n’a encore soumis son bilan.';
  const done = p.ordre1Done ? 'Conjoint 1' : 'Conjoint 2';
  const pending = p.ordre1Done ? 'Conjoint 2' : 'Conjoint 1';
  return `${done} a soumis son bilan. En attente de ${pending} pour afficher les indicateurs du couple.`;
});
const clientProfile = ref(null);
const clientStats = ref({
  analysesTotal: 0,
  soldeBudgetaire: 0,
  depensesAgregees: 0,
  revenusAgreges: 0,
  valeurPatrimoine: 0,
});

/**
 * Offre Duo (tableau de bord):
 * - duo_view=conjoint1|conjoint2 : KPI basés sur la dernière analyse du conjoint (ordre_duo=1|2)
 * - duo_view=couple             : KPI basés sur l’agrégation ménage (/duo-bilan)
 */
const duoDashboardView = computed(() => parseDuoDashboardView(route.query?.duo_view));

const ordreDuoForDashboard = computed(() => {
  return duoDashboardView.value === 'conjoint2' ? 2 : 1;
});

const revenusDepensesPeriodLabel = computed(() => {
  return '(dernière analyse en date)';
});

const userRole = ref(getUserRole() || '');
const isClientRole = computed(() => ['client', 'user'].includes(userRole.value));

/** Fallback si le Dashboard n’est pas sous DashboardLayout (inject absent). */
const clientTypeOffer = ref(null);
const clientFormuleLocal = ref('basique');
const financialDocumentsComplete = ref(null);

const resolvedClientOffer = computed(() => {
  const inj = injectedClientOffer?.value;
  const raw = inj != null && String(inj).trim() !== '' ? inj : (clientTypeOffer.value ?? 'persona');
  return normalizePayableOffer(raw);
});

const resolvedClientFormule = computed(() => {
  const inj = injectedClientFormule?.value;
  const local = clientFormuleLocal.value;
  if (inj === 'premium' || local === 'premium') return 'premium';
  return 'basique';
});

const resolvedOfferLabel = computed(() =>
  offerLabel(catalogOfferId(resolvedClientOffer.value, resolvedClientFormule.value))
);

const premiumTeasers = computed(() => {
  if (resolvedClientFormule.value !== 'premium') return [
  {
      key: 'Investisseur',
      title: 'Profil investisseur',
      text: 'formulaire du profil risque investisseur, réservé à l’offre Prémium.',
      cta: 'Evaluer mon profil',
    },
  ];
  if (resolvedClientOffer.value === 'pro') {
    return [
      {
        key: 'levee',
        title: 'Teaser de levée de fonds',
        text: 'Document de présentation pour vos opérations de financement, réservé à l’offre Pro Prémium. Votre conseiller le prépare à partir de votre dossier.',
        cta: 'Demander mon teaser',
      },
    ];
  }
  return [
    {
      key: 'projets',
      title: 'Teaser projets',
      text: 'Synthèse de vos projets à financer, réservée à l’offre Prémium. Demandez-la à votre conseiller.',
      cta: 'Demander mon teaser projets',
    },
  ];
});

/** Query non vide : l’utilisateur est sur un onglet poussé par le layout (?duo_view=…). On filtre alors par conjoint même si type_offre n’est pas encore monté (sinon KPI identiques C1/C2). */
function routeHasExplicitDuoView() {
  const raw = route.query?.duo_view;
  if (raw === undefined || raw === null) return false;
  const v = Array.isArray(raw) ? raw[0] : raw;
  return String(v ?? '').trim() !== '';
}

const useOrdreDuoFilter = computed(() => {
  if (duoDashboardView.value === 'couple') return false;
  if (resolvedClientOffer.value === 'duo') return true;
  return routeHasExplicitDuoView();
});

async function fetchClientOfferType() {
  if (!isClientRole.value) return;
  try {
    if (!isAuthenticatedSession()) {
      clientTypeOffer.value = 'persona';
      clientFormuleLocal.value = 'basique';
      return;
    }
    const { data } = await axios.get('/api/all-clients/', {
      withCredentials: true,
    });
    clientTypeOffer.value = normalizePayableOffer(data?.type_offre || 'persona');
    clientFormuleLocal.value = formuleFromApi(data);
    financialDocumentsComplete.value = data?.financial_documents_complete !== false;
  } catch {
    clientTypeOffer.value = 'persona';
    clientFormuleLocal.value = 'basique';
  }
}
const isAdminRole = computed(() => ['admin'].includes(userRole.value));
const isEmployeRole = computed(() => ['employe', 'saisie', 'validation'].includes(userRole.value));
const showStaffDashboard = computed(() => isAdminRole.value || isEmployeRole.value);

const contactConseillerRoute = computed(() => ({
  name: 'Parlez à un conseiller',
}));

const profilRisqueRoute = { name: 'Profil de risque investisseur'} ;

const questionnaireRoute = { name: 'Mon analyse patrimoniale' };
const resultatsRoute = { name: 'Analyses approfondies' };

const clientFirstName = computed(() => (clientProfile.value?.first_name || '').trim());

const clientGreeting = computed(() =>
  clientFirstName.value ? `Bonjour ${clientFirstName.value}` : 'Bonjour'
);

/** État du dossier dérivé de la dernière analyse (même appel que les KPI). */
const clientDossierState = ref(DOSSIER_STATE.QUESTIONNAIRE);
/** Étape du questionnaire lue sur le brouillon serveur (`null` si aucun brouillon). */
const clientWizardStepId = ref(null);
/** Numéro d'étape 1-indexé, `0` si aucun brouillon exploitable. */
const clientWizardStep = computed(() => wizardStepNumber(clientWizardStepId.value));

const clientGreetingSub = computed(() => {
  const offer = resolvedOfferLabel.value;
  if (clientDossierState.value === DOSSIER_STATE.PUBLISHED) {
    return `Offre ${offer} votre analyse est disponible, explorez vos espaces ci-dessous.`;
  }
  if (clientDossierState.value === DOSSIER_STATE.REVIEW) {
    return `Offre ${offer} votre dossier est entre nos mains. Voici ce que vous pouvez déjà consulter.`;
  }
  if (resolvedClientOffer.value === 'duo') {
    return `Offre ${offer} suivez le dossier de chaque conjoint, puis la vue couple.`;
  }
  if (resolvedClientOffer.value === 'pro') {
    return `Offre ${offer} suivez l’articulation entre votre activité et votre patrimoine.`;
  }
  return `Offre ${offer} voici où en est votre dossier et les prochaines étapes utiles.`;
});

/**
 * Avancement affiché par la jauge. Tant que le questionnaire n'est pas transmis,
 * il vaut la progression d'étape : on n'invente pas un taux de remplissage,
 * l'API n'expose pas le détail des champs remplis.
 */
const dossierProgressPercent = computed(() => {
  // Onglet Couple : la progression d'étape individuelle n'a pas de sens ici (ce n'est
  // pas "mon" questionnaire) — on reflète le vrai statut de soumission du couple (0/50/100%),
  // la même donnée que la carte duoCoupleProgress, jamais une estimation par étape.
  if (duoDashboardView.value === 'couple') {
    return duoCoupleProgress.value ? duoCoupleProgress.value.percent : 100;
  }
  if (clientDossierState.value !== DOSSIER_STATE.QUESTIONNAIRE) return 100;
  if (!clientWizardStep.value) return 0;
  // 100% réservé à une soumission réelle : la dernière étape du wizard (page6_submit,
  // "Validation finale") est un écran de relecture avant envoi, pas l'envoi lui-même.
  // Sans ce plafond, l'atteindre sans cliquer « Envoyer » affichait déjà 100%.
  const pct = Math.round((clientWizardStep.value / WIZARD_STEP_TOTAL) * 100);
  return Math.min(pct, 99);
});

const dossierStatusCard = computed(() => {
  if (clientDossierState.value === DOSSIER_STATE.PUBLISHED) {
    return {
      title: 'Votre analyse est disponible',
      text: 'Vos résultats et les recommandations de votre conseiller sont prêts à être consultés.',
      cta: 'Voir mes résultats',
      to: resultatsRoute,
      ghost: false,
      note: 'Un code de validation à 6 chiffres, reçu par e-mail, vous sera demandé.',
    };
  }
  if (clientDossierState.value === DOSSIER_STATE.REVIEW) {
    return {
      title: 'Votre dossier est entre nos mains',
      text: 'Votre conseiller prépare votre analyse, en général sous 72 h maximum. Vous recevrez un e-mail dès qu’elle sera disponible.',
      cta: 'Voir l’état de mon dossier',
      to: resultatsRoute,
      ghost: true,
    };
  }
  if (!clientWizardStep.value) {
    return {
      title: 'Commençons votre dossier',
      text: 'Le questionnaire patrimonial se remplit à votre rythme : vos réponses sont enregistrées à chaque étape.',
      cta: 'Démarrer mon questionnaire',
      to: questionnaireRoute,
      ghost: false,
    };
  }
  const label = wizardStepLabel(clientWizardStepId.value);
  return {
    title: 'Votre dossier avance',
    text: `Étape ${clientWizardStep.value} sur ${WIZARD_STEP_TOTAL}${label ? ` — ${label}` : ''}. Reprenez quand vous le souhaitez, rien n’est perdu.`,
    cta: 'Continuer mon questionnaire',
    to: questionnaireRoute,
    ghost: false,
  };
});

/** Le graphique vide renvoie vers la prochaine action utile, pas vers une page morte. */
const chartEmptyCta = computed(() => {
  if (clientDossierState.value === DOSSIER_STATE.QUESTIONNAIRE) {
    return { to: questionnaireRoute, label: 'Continuer mon questionnaire' };
  }
  if (clientDossierState.value === DOSSIER_STATE.REVIEW) {
    return { to: resultatsRoute, label: 'Voir l’état de mon dossier' };
  }
  return { to: contactConseillerRoute.value, label: 'Contacter mon conseiller' };
});

/**
 * Sans analyse, les montants ne valent pas 0 : ils ne sont pas encore calculés.
 * Les cartes affichent alors « — » + badge, jamais un « 0 F CFA » trompeur.
 */
const hasClientIndicators = computed(() => (clientStats.value.analysesTotal || 0) > 0);

const INDICATOR_PENDING_BADGE = 'En attente';

const clientStatCards = computed(() => {
  const s = clientStats.value;
  const ready = hasClientIndicators.value;
  const amount = (value) => (ready ? value : null);
  const pendingBadge = ready ? '' : INDICATOR_PENDING_BADGE;
  return [
    {
      key: 'analyses-total',
      label: 'Analyses',
      value: s.analysesTotal,
      icon: ChartBarIcon,
      accent: 'primary',
      suffix: '',
      status: '',
      statusBadge: '',
      tooltip: 'Nombre d\'analyses patrimoniales réalisées. Plus vous en avez, plus votre suivi est précis.',
    },
    {
      key: 'solde',
      label: 'Solde budgétaire annuel',
      value: amount(s.soldeBudgetaire),
      icon: BanknotesIcon,
      accent: 'orange',
      suffix: ' F CFA',
      status: ready && s.soldeBudgetaire < 0 ? 'negative' : '',
      statusBadge: ready
        ? (s.soldeBudgetaire < 0 ? 'À surveiller' : '')
        : INDICATOR_PENDING_BADGE,
      tooltip: 'Votre solde budgétaire est la différence entre revenus et dépenses. Un solde négatif signifie que vos dépenses dépassent vos revenus. L\'idéal est d\'avoir un solde positif d\'au moins 10% de vos revenus pour constituer une épargne.',
    },
    {
      key: 'depenses',
      label: 'Dépenses annuelles',
      value: amount(s.depensesAgregees),
      icon: DocumentTextIcon,
      accent: 'orange',
      suffix: ' F CFA',
      status: '',
      statusBadge: pendingBadge,
      tooltip: 'Total de vos dépenses agrégées sur la période. À comparer avec vos revenus pour équilibrer votre budget.',
    },
    {
      key: 'revenus',
      label: 'Revenus annuels',
      value: amount(s.revenusAgreges),
      icon: ChartBarIcon,
      accent: 'green',
      suffix: ' F CFA',
      status: '',
      statusBadge: pendingBadge,
      tooltip: 'Total de vos revenus sur la période. Base de calcul pour votre capacité d\'épargne et votre solde budgétaire.',
    },
    {
      key: 'valeur-nette',
      label: 'Valeur nette (actifs-passifs)',
      value: amount(s.valeurPatrimoine),
      icon: ChartPieIcon,
      accent: 'blue',
      suffix: ' F CFA',
      status: '',
      statusBadge: pendingBadge,
      tooltip:
        'Somme des actifs moins les passifs déclarés dans vos analyses (immobilier, financier, entreprise, moins crédit immobilier). Les revenus du budget ne suffisent pas à ce calcul : complétez l’onglet patrimoine dans le formulaire.',
    },
  ];
});

const revenusDepensesChartRef = ref(null);
let revenusDepensesChartInstance = null;

const revenusDepensesChartReady = computed(() => !clientLoading.value);

const hasRevenusOrDepenses = computed(() => {
  const r = clientStats.value.revenusAgreges || 0;
  const d = clientStats.value.depensesAgregees || 0;
  return r > 0 || d > 0;
});

function buildRevenusDepensesChart() {
  const el = revenusDepensesChartRef.value;
  if (!el) return;
  const r = Number(clientStats.value.revenusAgreges) || 0;
  const d = Number(clientStats.value.depensesAgregees) || 0;
  if (revenusDepensesChartInstance) {
    try { revenusDepensesChartInstance.detach(); } catch (_) {}
    revenusDepensesChartInstance = null;
  }
  const maxVal = Math.max(r, d, 1);
  revenusDepensesChartInstance = new Chartist.Bar(
    el,
    {
      labels: ['Revenus', 'Dépenses'],
      series: [[r, d]],
    },
    {
      axisY: {
        onlyInteger: true,
        scaleMinSpace: 44,
        low: 0,
        high: maxVal * 1.15,
        labelInterpolationFnc: (value) => (value >= 1e6 ? `${(value / 1e6).toFixed(1)} M` : value >= 1e3 ? `${(value / 1e3).toFixed(0)} K` : String(value)),
      },
      axisX: { showGrid: false },
      seriesBarDistance: 28,
      chartPadding: { top: 20, right: 24, bottom: 28, left: 48 },
    }
  );
  revenusDepensesChartInstance.on('draw', (data) => {
    if (data.type === 'bar') {
      // Vert « succès » et or de la charte plutôt que les teintes Tailwind par défaut.
      const fill = data.index === 0 ? '#2d8659' : '#b8941f';
      data.element.attr({ style: `stroke: ${fill}; fill: ${fill};` });
    }
  });
}

const revenusDepensesContext = computed(() => {
  const r = clientStats.value.revenusAgreges || 0;
  const d = clientStats.value.depensesAgregees || 0;
  const diff = d - r;
  const pct = r > 0 ? Math.round((diff / r) * 100) : (d > 0 ? 100 : 0);
  if (d > r && r > 0) {
    return {
      message: `Les dépenses dépassent les revenus de ${pct}%.`,
      critical: true,
      recommendation: `Réduire les dépenses de ${formatMontant(diff)} F CFA pour équilibrer le budget.`,
    };
  }
  if (r > d && d > 0) {
    const surplus = r - d;
    return {
      message: `Vos revenus dépassent vos dépenses de ${formatMontant(surplus)} F CFA.`,
      critical: false,
      recommendation: 'Vous pouvez affecter ce surplus à l\'épargne ou au remboursement d\'emprunts.',
    };
  }
  if (r === 0 && d === 0) {
    return { message: 'Aucune donnée revenus/dépenses pour l\'instant.', critical: false, recommendation: 'Complétez vos informations pour voir la comparaison.' };
  }
  return { message: '', critical: false, recommendation: '' };
});

function formatMontant(n) {
  return Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 });
}

const stats = ref({
  totalClients: 0,
  pendingSignatures: 0,
  incompleteFiles: 0,
  activeOffers: 0,
  totalMessages: 0,
  formulairesEntames: 0,
  formulairesTermines: 0,
  totalUtilisateur: 0,
  totalEmploye: 0,
});

const selectedClient = ref(null);
const clientsDocuments = ref([]);
const signaturesList = ref([]);
const messagesList = ref([]);
const analysesList = ref([]);

const unifiedStatCards = computed(() => [
  {
    key: 'clients',
    label: 'Total clients',
    value: stats.value.totalClients,
    icon: UserGroupIcon,
    accent: 'green',
    to: '/analyse-patrimoniale/gestion-clients?filter=all',
  },
  {
    key: 'incomplete',
    label: 'Dossiers incomplets',
    value: stats.value.incompleteFiles,
    icon: DocumentTextIcon,
    accent: 'blue',
    to: '/analyse-patrimoniale/gestion-clients',
  },
  {
    key: 'messages',
    label: 'Messages envoyés',
    value: stats.value.totalMessages,
    icon: EnvelopeIcon,
    accent: 'blue',
    to: '/analyse-patrimoniale/requetes-clients',
  },
  {
    key: 'entames',
    label: 'Formulaires entamés',
    value: stats.value.formulairesEntames,
    icon: DocumentTextIcon,
    accent: 'orange',
    to: '/analyse-patrimoniale/gestion-formulaires?filter=entame',
  },
  {
    key: 'termines',
    label: 'Formulaires terminés',
    value: stats.value.formulairesTermines,
    icon: DocumentCheckIcon,
    accent: 'green',
    to: '/analyse-patrimoniale/gestion-formulaires?filter=termine',
  },
  {
    key: 'employes',
    label: 'Total employés',
    value: stats.value.totalEmploye,
    icon: UsersIcon,
    accent: 'purple',
    to: '/analyse-patrimoniale/gestion-utilisateur',
  },
  {
    key: 'utilisateurs',
    label: 'Total utilisateurs',
    value: stats.value.totalUtilisateur,
    icon: Cog6ToothIcon,
    accent: 'primary',
    to: '/analyse-patrimoniale/gestion-utilisateur',
  },
]);

const selectedClientDocStatus = computed(() => {
  if (!selectedClient.value) return null;
  const login = selectedClient.value.login || selectedClient.value.username;
  const doc = clientsDocuments.value.find(
    (d) => (d.login || d.client_login || '').toLowerCase() === (login || '').toLowerCase()
  );
  if (!doc) return { offerName: '—' };
  return {
    formulaire_statut: doc.formulaire_statut,
    photo: doc.photo,
    justificatif_identite: doc.justificatif_identite,
    justificatif_revenu: doc.justificatif_revenu,
    releve_bancaire: doc.releve_bancaire,
    justificatif_residence: doc.justificatif_residence,
    offerName: doc.offer_name || doc.offre || 'Persona',
  };
});

const selectedClientLogs = computed(() => {
  if (!selectedClient.value) return [];
  const login = selectedClient.value.login || selectedClient.value.username;
  return messagesList.value
    .filter(
      (m) =>
        (m.client_login || m.client_name || '').toLowerCase() === (login || '').toLowerCase()
    )
    .map((m, i) => ({
      id: m.id || i,
      subject: m.subject || m.sujet,
      created_at: m.created_at,
    }))
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
});

const selectedClientSignatures = computed(() => {
  if (!selectedClient.value) return [];
  const login = selectedClient.value.login || selectedClient.value.username;
  const docs = signaturesList.value.documents || signaturesList.value || [];
  return (Array.isArray(docs) ? docs : [])
    .filter(
      (s) => (s.client_login || s.login || '').toLowerCase() === (login || '').toLowerCase()
    )
    .map((s, i) => ({
      id: s.id || i,
      document_name: s.document_name || s.document_type,
      statut: s.statut || s.status,
      signed_at: s.signed_at || s.date_signed,
    }))
    .sort((a, b) => new Date(b.signed_at || 0) - new Date(a.signed_at || 0));
});

function onSelectClient(client) {
  selectedClient.value = client;
}

function openAllLogs() {
  // Could open modal or route to full messages view
  console.log('View all logs');
}

function openAllSignatures() {
  console.log('View all signatures');
}

/**
 * Étape courante du questionnaire, écrite par le formulaire sous `wizard_step_id`.
 * Le brouillon Duo est rangé sous `duo_1` / `duo_2` (voir FormDraftView) : on retombe
 * sur ces sous-clés quand la requête n'a pas ciblé un conjoint précis.
 */
function readWizardStepId(draftData) {
  if (!draftData || typeof draftData !== 'object') return null;
  const direct = draftData.wizard_step_id;
  if (direct) return direct;
  for (const key of ['duo_1', 'duo_2', 'persona']) {
    const nested = draftData[key];
    if (nested && typeof nested === 'object' && nested.wizard_step_id) return nested.wizard_step_id;
  }
  return null;
}

async function fetchClientDraftProgress() {
  if (!isClientRole.value) return;
  try {
    if (!isAuthenticatedSession()) return;
    const params = useOrdreDuoFilter.value ? { ordre_duo: ordreDuoForDashboard.value } : {};
    const { data } = await axios.get('/api/form-draft/', { withCredentials: true, params });
    clientWizardStepId.value = readWizardStepId(data?.data);
  } catch {
    clientWizardStepId.value = null;
  }
}

async function fetchClientProfile() {
  if (!isClientRole.value) return;
  try {
    if (!isAuthenticatedSession()) return;
    const res = await axios.get('/api/profile/', { withCredentials: true });
    const data = res.data || {};
    clientProfile.value = {
      first_name: data.first_name ?? data.prenom ?? '',
      last_name: data.last_name ?? data.nom ?? '',
    };
  } catch {
    clientProfile.value = null;
  }
}

/**
 * KPI tableau de bord client : `latest=true` pour rester aligné avec le bilan / analyse la plus récente
 * (sinon le backend agrège toutes les analyses et les montants ne reflètent pas l’analyse courante).
 * @param {{ silent?: boolean }} opts - silent : pas de squelette plein écran (rafraîchissement discret).
 */
async function fetchClientDashboard(opts = {}) {
  const silent = !!opts.silent;
  if (!isClientRole.value) return;
  if (silent) {
    clientRefreshing.value = true;
  } else {
    clientLoading.value = true;
  }
  clientError.value = null;
  clientBilanLocked.value = false;
  duoCoupleProgress.value = null;
  try {
    if (!isAuthenticatedSession()) throw new Error('Non authentifié');
    if (duoDashboardView.value === 'couple') {
      const res = await axios.get('/api/analyse-patrimoniale/duo-bilan/', {
        withCredentials: true,
      });
      if (res.data?.missing) {
        duoCoupleProgress.value = {
          percent: Number(res.data.progress_percent) || 0,
          ordre1Done: !res.data.missing.ordre_duo_1,
          ordre2Done: !res.data.missing.ordre_duo_2,
        };
        clientStats.value = {
          analysesTotal: 0,
          soldeBudgetaire: 0,
          depensesAgregees: 0,
          revenusAgreges: 0,
          valeurPatrimoine: 0,
        };
        // REVIEW seulement si un conjoint a réellement soumis (50%) : sinon (0%) la jauge
        // globale du dashboard (dossierProgressPercent) afficherait 100% à tort, puisqu'elle
        // se base sur `clientDossierState !== QUESTIONNAIRE`.
        clientDossierState.value = duoCoupleProgress.value.percent > 0
          ? DOSSIER_STATE.REVIEW
          : DOSSIER_STATE.QUESTIONNAIRE;
        return;
      }
      const menage = res.data?.menage || {};
      const budget = menage?.budget || {};
      const patrimoine = menage?.patrimoine || {};
      clientStats.value = {
        analysesTotal: 1,
        soldeBudgetaire: Math.round(Number(budget.solde_annuel) || 0),
        depensesAgregees: Math.round(Number(budget.depenses_annuel) || 0),
        revenusAgreges: Math.round(Number(budget.revenus_annuel) || 0),
        valeurPatrimoine: Math.round(Number(patrimoine.valeur_patrimoine) || 0),
      };
      // L'agrégation ménage ne porte pas de statut : on considère le dossier publié
      // dès qu'elle renvoie des montants, sinon la revue est encore en cours.
      const c = clientStats.value;
      clientDossierState.value =
        c.revenusAgreges || c.depensesAgregees || c.valeurPatrimoine
          ? DOSSIER_STATE.PUBLISHED
          : DOSSIER_STATE.REVIEW;
    } else {
      const params = { latest: 'true' };
      if (useOrdreDuoFilter.value) {
        params.ordre_duo = ordreDuoForDashboard.value;
      }
      const res = await axios.get('/api/analyse-patrimoniale/', {
        withCredentials: true,
        params,
      });
      const analyses = res.data?.analyses ?? [];
      const stats = res.data?.stats ?? {};
      const statsMeta = res.data?.stats_meta ?? null;
      const latestAnalyse = normalizeLatestAnalyse(res.data);
      const analysesTotal =
        statsMeta?.analyses_count != null
          ? Number(statsMeta.analyses_count) || 0
          : (Array.isArray(analyses) ? analyses.length : 0);
      clientStats.value = {
        analysesTotal,
        soldeBudgetaire: Math.round(Number(stats.solde_budgetaire) || 0),
        depensesAgregees: Math.round(Number(stats.depenses_agreges) || 0),
        revenusAgreges: Math.round(Number(stats.revenus_agreges) || 0),
        valeurPatrimoine: Math.round(Number(stats.valeur_patrimoine) || 0),
      };
      // `details` peut manquer (API antérieure) : le nombre d'analyses prouve à lui seul
      // que le questionnaire a été transmis, on ne renvoie pas le client au formulaire.
      clientDossierState.value =
        !latestAnalyse && analysesTotal > 0
          ? DOSSIER_STATE.REVIEW
          : deriveDossierState(latestAnalyse);
    }
  } catch (e) {
    if (e?.response?.data?.code === 'bilan_code_required') {
      clientBilanLocked.value = true;
      // Le gate code n’existe que pour un bilan déjà publié.
      clientDossierState.value = DOSSIER_STATE.PUBLISHED;
    } else {
      // safeErrorMessage : jamais le texte brut du serveur/JS ici (l'ancien code
      // affichait par ex. "Backend injoignable. Vérifiez que le proxy vue.config.js
      // pointe vers le bon port." à un client final — une instruction de développeur).
      clientError.value = safeErrorMessage(e, 'Impossible de charger les indicateurs du tableau de bord.');
    }
    clientStats.value = {
      analysesTotal: 0,
      soldeBudgetaire: 0,
      depensesAgregees: 0,
      revenusAgreges: 0,
      valeurPatrimoine: 0,
    };
  } finally {
    clientLoading.value = false;
    clientRefreshing.value = false;
    nextTick(() => updateRevenusDepensesChart());
  }
}

function refreshClientKpis() {
  fetchClientDashboard({ silent: true });
  fetchClientDraftProgress();
}

async function fetchUnifiedData() {
  if (!showStaffDashboard.value) {
    loading.value = false;
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    if (!isAuthenticatedSession()) {
      error.value = getErrorMessage({ response: { status: 401 } }).message;
      loading.value = false;
      return;
    }
    const headers = { withCredentials: true };

    if (isAdminRole.value) {
      const [dashboardRes, clientsDocRes, sigRes, messagesRes, analysesRes] = await Promise.all([
        axios.get('/api/admin/stats-dashboard/', { withCredentials: true }).catch((e) => ({ error: e })),
        axios.get('/api/admin/clients-documents/', { withCredentials: true }).catch((e) => ({ error: e })),
        axios.get('/api/admin/signatures/', { withCredentials: true }).catch((e) => ({ error: e })),
        axios.get('/api/messages/', { withCredentials: true }).catch((e) => ({ error: e })),
        axios.get('/api/toutes-analyses/', { withCredentials: true }).catch((e) => ({ error: e })),
      ]);

      const d = dashboardRes?.data;
      stats.value.totalClients = d?.total_clients ?? 0;
      stats.value.totalMessages = d?.total_messages ?? 0;
      stats.value.formulairesEntames = d?.formulaires_entames ?? 0;
      stats.value.formulairesTermines = d?.formulaires_termines ?? 0;
      stats.value.totalEmploye = d?.total_employes ?? 0;
      stats.value.totalUtilisateur = d?.total_utilisateurs ?? (stats.value.totalClients + stats.value.totalEmploye);
      const analysesRaw = analysesRes?.data;
      const offersCount = Array.isArray(analysesRaw)
        ? analysesRaw.length
        : (Array.isArray(analysesRaw?.analyses) ? analysesRaw.analyses.length : (analysesRaw?.count ?? 0));
      stats.value.activeOffers = offersCount || 0;

      const docList = clientsDocRes?.data?.results ?? clientsDocRes?.data ?? [];
      clientsDocuments.value = Array.isArray(docList) ? docList : [];
      const incomplete = clientsDocuments.value.filter(
        (c) =>
          (c.formulaire_statut || '') !== 'termine' ||
          !c.photo ||
          !c.justificatif_identite ||
          !c.justificatif_revenu ||
          !c.releve_bancaire ||
          !c.justificatif_residence
      );
      stats.value.incompleteFiles = incomplete.length;

      const sigData = sigRes?.data;
      const docs = sigData?.documents ?? sigData?.results ?? sigData ?? [];
      signaturesList.value = { documents: Array.isArray(docs) ? docs : [] };
      const pending = (signaturesList.value.documents || []).filter(
        (s) => (s.statut || s.status || '').toLowerCase() !== 'signe'
      );
      stats.value.pendingSignatures = pending.length;

      const msgList = messagesRes?.data?.results ?? messagesRes?.data ?? [];
      messagesList.value = Array.isArray(msgList) ? msgList : [];
    } else {
      // Employé (saisie/validation/employe): pas d'accès aux endpoints admin, on charge juste ses messages si besoin
      const messagesRes = await axios.get('/api/messages/', { withCredentials: true }).catch((e) => ({ error: e }));
      const msgList = messagesRes?.data?.results ?? messagesRes?.data ?? [];
      messagesList.value = Array.isArray(msgList) ? msgList : [];

      // Stats neutres (les composants dédiés employé peuvent être ajoutés plus tard)
      stats.value.activeOffers = 0;
      stats.value.pendingSignatures = 0;
      stats.value.incompleteFiles = 0;
      stats.value.formulairesEntames = 0;
      stats.value.formulairesTermines = 0;
      stats.value.totalMessages = messagesList.value.length;
      clientsDocuments.value = [];
      signaturesList.value = { documents: [] };
    }
  } catch (e) {
    error.value = getErrorMessage(e).message;
  } finally {
    loading.value = false;
  }
}

function updateRevenusDepensesChart() {
  if (!isClientRole.value || !hasRevenusOrDepenses.value) return;
  nextTick(() => buildRevenusDepensesChart());
}

function handleVisibilityChange() {
  if (document.visibilityState !== 'visible') return;
  if (!isClientRole.value) return;
  const path = route.fullPath || '';
  if (!path.includes('/dashboards')) return;
  fetchClientDashboard({ silent: true });
}

onMounted(async () => {
  await syncEmployeProfileFromApi();
  userRole.value = getUserRole() || '';
  if (showStaffDashboard.value) fetchUnifiedData();
  else loading.value = false;
  if (isClientRole.value) {
    fetchClientProfile();
    await fetchClientOfferType();
    fetchClientDashboard();
    fetchClientDraftProgress();
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

onActivated(async () => {
  if (isClientRole.value) {
    // Toujours rafraîchir (pas seulement au premier chargement) : financial_documents_complete
    // change dès que le client ajoute un justificatif manquant, sinon le bandeau de rappel
    // reste affiché même après correction, jusqu'au prochain rechargement complet de page.
    await fetchClientOfferType();
    fetchClientDashboard({ silent: true });
    fetchClientProfile();
    fetchClientDraftProgress();
  }
});

watch(
  () => route.fullPath,
  (newPath, oldPath) => {
    if (!isClientRole.value) return;
    if (!newPath.includes('/dashboards')) return;
    if (newPath === oldPath) return;
    fetchClientDashboard({ silent: true });
  }
);

/** Dès que le layout expose `type_offre=duo`, réappliquer le filtre `ordre_duo` (évite KPI identiques C1/C2). */
watch(
  resolvedClientOffer,
  (offer, prev) => {
    if (!isClientRole.value) return;
    if (!(route.path || '').includes('/dashboards')) return;
    if (offer === prev) return;
    fetchClientDashboard({ silent: true });
  },
  { flush: 'post' }
);

/** Onglets Duo : `duo_view` change sans altérer le path — recharger les KPI. */
watch(
  () => route.query?.duo_view,
  () => {
    if (!isClientRole.value) return;
    const p = route.path || '';
    if (!p.includes('/dashboards')) return;
    fetchClientDashboard({ silent: true });
  }
);

watch([clientLoading, hasRevenusOrDepenses, clientBilanLocked], () => {
  if (clientBilanLocked.value) return;
  if (revenusDepensesChartReady.value && hasRevenusOrDepenses.value) updateRevenusDepensesChart();
});
</script>

<style scoped>
.dashboard-page {
  @apply space-y-6 sm:space-y-8;
}

.dashboard-page__section-head {
  @apply flex flex-wrap items-baseline justify-between gap-3;
  margin-bottom: var(--space-3);
}
.dashboard-page__section-head .client-section__label {
  margin-bottom: 0;
}
.dashboard-page__refresh-btn {
  @apply inline-flex items-center gap-2 shrink-0 px-3 py-2 rounded-xl text-sm font-medium border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-colors disabled:opacity-60 disabled:cursor-not-allowed;
}
.dashboard-page__refresh-spinner {
  display: inline-block;
  width: 0.875rem;
  height: 0.875rem;
  border: 2px solid rgba(37, 99, 235, 0.25);
  border-top-color: rgb(37 99 235);
  border-radius: 9999px;
  animation: dashboard-kpi-spin 0.65s linear infinite;
}
@keyframes dashboard-kpi-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Mobile: 1 col, Tablet: 2, Laptop: 3, Desktop: 4–6 with smooth reflow */
.dashboard-page__grid {
  @apply grid gap-3 sm:gap-4 lg:gap-5;
  grid-template-columns: 1fr;
}
.dashboard-page__grid--stats {
  grid-template-columns: repeat(2, 1fr);
}
@media (min-width: 640px) {
  .dashboard-page__grid--stats {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (min-width: 768px) {
  .dashboard-page__grid--stats {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (min-width: 1024px) {
  .dashboard-page__grid--stats {
    grid-template-columns: repeat(4, 1fr);
  }
}
@media (min-width: 1280px) {
  .dashboard-page__grid--stats {
    grid-template-columns: repeat(5, 1fr);
  }
}

.dashboard-page__master-detail {
  @apply grid gap-4 lg:gap-6 min-h-0;
  grid-template-columns: 1fr;
  min-height: 420px;
}
@media (min-width: 1024px) {
  .dashboard-page__master-detail {
    grid-template-columns: 320px 1fr;
  }
}
.dashboard-page__master-wrap {
  @apply min-h-[320px] lg:min-h-[480px];
}
.dashboard-page__detail-wrap {
  @apply min-h-[320px] lg:min-h-[480px];
}

.dashboard-page__error {
  @apply p-6 rounded-2xl bg-red-50 border border-red-100 text-center;
}

.dashboard-page__stats-note {
  @apply text-xs mt-3 mb-0;
  font-family: var(--font-body);
  color: var(--muted);
}

.dashboard-page__section-title {
  @apply text-base sm:text-lg font-semibold text-slate-800 mb-3 sm:mb-4;
}

.dashboard-page__table {
  @apply animate-fade-in;
}

.dashboard-page__client-header {
  margin-bottom: var(--space-5);
}

.client-docs-reminder {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-5);
  padding: var(--space-3) var(--space-4);
  border-radius: 12px;
  border: 1px solid var(--brand-gold, #c9a227);
  background: color-mix(in srgb, var(--brand-gold, #c9a227) 10%, #fff);
  color: var(--brand-navy, #01263e);
  font-family: var(--font-body);
  font-size: 0.9rem;
  text-decoration: none;
  transition: box-shadow 0.15s;
}
.client-docs-reminder:hover {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--brand-gold, #c9a227) 35%, transparent);
}
.client-docs-reminder__icon {
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
  color: var(--brand-gold, #c9a227);
}

.client-status__note {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-body);
  font-size: 0.8125rem;
  color: var(--client-text-muted, #6b6155);
  margin: var(--space-2) 0 0;
}
.client-status__note-icon {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
}

.dashboard-page__stat-card-entrance {
  animation: stat-card-entrance 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
}
@keyframes stat-card-entrance {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Client : bandeau compact d'indicateurs, 2 par ligne sur mobile, 5 sur large écran */
.dashboard-page__grid--client {
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
}

/* ---- Hub client (layout principal + rail) — styles dans client-space.css ---- */
.client-shell .dashboard-page__stats--client {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}
.client-shell .dashboard-page__refresh-btn {
  background: transparent;
  border-color: var(--client-line);
  border-radius: var(--radius-pill);
  color: var(--brand-navy);
  box-shadow: none;
  padding: 6px 14px;
}
.client-shell .dashboard-page__refresh-btn:hover:not(:disabled) {
  background: var(--client-surface-card-hover);
  border-color: var(--brand-navy);
}
.client-shell .dashboard-page__refresh-spinner {
  border-color: var(--brand-gold-soft);
  border-top-color: var(--brand-gold-dark);
}

/* Revenus vs Dépenses */
.card-revenus-depenses {
  @apply rounded-2xl bg-white border border-slate-100/80 overflow-hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  margin-top: 1.5rem;
  animation: stat-card-entrance 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s backwards;
}
.card-revenus-depenses__title {
  @apply text-base font-semibold text-slate-800 px-5 py-4 border-b border-slate-100 flex flex-wrap items-baseline gap-2;
}
.card-revenus-depenses__period {
  @apply text-xs font-normal text-slate-500;
}
.card-revenus-depenses__content {
  @apply p-5 space-y-4;
}
.card-revenus-depenses__chart-wrap {
  @apply min-h-[200px];
}
.card-revenus-depenses__chart {
  @apply w-full;
  height: 220px;
}
.card-revenus-depenses__chart :deep(.ct-label) {
  @apply fill-slate-600 text-xs;
}
.card-revenus-depenses__legend-inline {
  @apply flex flex-wrap gap-4 sm:gap-6 mt-3 pt-3 border-t border-slate-100;
}
.card-revenus-depenses__legend-item {
  @apply text-sm font-medium flex items-center gap-2;
}
.card-revenus-depenses__legend-item .card-revenus-depenses__dot {
  @apply w-2.5 h-2.5 rounded-full flex-shrink-0;
}
.card-revenus-depenses__legend--revenus .card-revenus-depenses__dot {
  @apply bg-emerald-500;
}
.card-revenus-depenses__legend--depenses .card-revenus-depenses__dot {
  @apply bg-amber-500;
}
.card-revenus-depenses__legend--revenus {
  @apply text-emerald-700;
}
.card-revenus-depenses__legend--depenses {
  @apply text-amber-700;
}
.card-revenus-depenses__empty {
  @apply py-8 text-center;
}
.card-revenus-depenses__empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--brand-gold-soft);
  color: var(--brand-navy);
  margin-bottom: var(--space-3);
}
.card-revenus-depenses__empty-icon :deep(svg) {
  width: 22px;
  height: 22px;
}
.card-revenus-depenses__empty-text {
  font-family: var(--font-body);
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--client-text-muted, #6b6155);
  max-width: 34rem;
  margin: 0 auto var(--space-4);
}
.card-revenus-depenses__message {
  @apply text-sm font-medium text-slate-700 pt-1;
}
.card-revenus-depenses__message--critical {
  @apply text-red-600;
}
.card-revenus-depenses__recommendation {
  @apply text-sm text-slate-600 bg-slate-50 rounded-xl px-4 py-3 border border-slate-100;
}

.client-shell .card-revenus-depenses {
  background: var(--client-surface-card);
  border: 1px solid var(--client-line);
  border-radius: var(--client-radius-lg);
  box-shadow: var(--client-shadow, none);
  margin-top: 0;
}
/* La zone de données repose sur un panneau blanc : le graphique se lit mieux
   sur fond neutre que sur le crème de la carte. */
.client-shell .card-revenus-depenses__chart-wrap {
  background: var(--paper);
  border: 1px solid var(--client-line);
  border-radius: var(--client-radius);
  padding: var(--space-4);
}
.client-shell .card-revenus-depenses__legend-inline {
  border-top-color: var(--client-line);
}
.client-shell .card-revenus-depenses__legend--revenus {
  color: var(--success);
}
.client-shell .card-revenus-depenses__legend--depenses {
  color: var(--brand-gold-dark, #b8941f);
}
.client-shell .card-revenus-depenses__legend--revenus .card-revenus-depenses__dot {
  background: var(--success);
}
.client-shell .card-revenus-depenses__legend--depenses .card-revenus-depenses__dot {
  background: var(--brand-gold-dark);
}
.client-shell .card-revenus-depenses__period {
  font-family: var(--font-body);
  color: var(--client-text-muted);
  margin: 0;
}
.client-shell .card-revenus-depenses__message,
.client-shell .card-revenus-depenses__recommendation {
  font-family: var(--font-body);
}
.client-shell .card-revenus-depenses__recommendation {
  background: rgba(255, 255, 255, 0.6);
  border-color: var(--client-line);
  color: var(--client-text);
}
</style>
